// Shared client-side env bindings.
//
// makeBaseEnv guarantees every import LINKS; this module guarantees the
// client-role imports BEHAVE identically across tiers. The headless
// dummy client (@multinostalgia/server) and the browser client
// (@multinostalgia/web) used to each hand-write these ~25 bindings —
// the same code twice, drifting one log-prefix at a time. Now both
// build their env as
//
//   { ...makeBaseEnv(h, …), ...makeClientCommonEnv(h, hooks), ...tierSpecific }
//
// where tierSpecific is only what genuinely differs: the transport
// (send_client over `ws` vs the browser WebSocket), fetch, hop and
// disconnect.
//
// makeDbEnv is split out because it serves more than clients: ANY host
// with a MunosKvBackend (the server's ScriptInstance over SQLite, the
// browser client over LocalStorage, the dummy and the reference host
// over the in-memory store) binds db_get/set/del/list through it.
import { allocString, readBytesPtr, readStringPtr, } from "./memory-utils.js";
import { SHARED_SCOPE, pageKeys } from "./kv.js";
const COORD_REGIONS = ["PRG_ROM", "PRG_RAM", "CPU"];
function encodeCoord(regionId, offset) {
    return ((regionId & 0xf) << 28) | (offset & 0x0fffffff);
}
function decodeCoord(v) {
    const regionId = (v >>> 28) & 0xf;
    return { region: COORD_REGIONS[regionId] ?? "CPU", offset: v & 0x0fffffff };
}
// db_* against any MunosKvBackend. The import reads its arguments from
// wasm memory synchronously (the memory may grow under a later op), then
// kicks off the async backend call and registers it with the queue. It
// returns void immediately; the completion is pushed when the backend
// resolves, and the callback fires on the next drain (between event
// handlers), per docs/persistence.md §Callbacks. An infrastructure
// failure (rejected promise) reports success=false plus an infraError so
// db_error fires if the script declared it.
//
// `scope` is this host's private identity (a fetch URL client-side, a
// `.munos` basename server-side). This is the ONE place the public-tier
// routing lives: a namespace beginning with `@` is not private — it goes
// to SHARED_SCOPE, the single space every script shares — while every
// other namespace stays inside this host's own scope. Because an `@` name
// always routes to the shared tier, a *private* namespace can never begin
// with `@`; the reservation needs no separate check. The script never
// sees its scope; the namespace + key it passes are unchanged on the wire.
export function makeDbEnv(h, asyncOps, kv, scope) {
    const scopeOf = (ns) => (ns.startsWith("@") ? SHARED_SCOPE : scope);
    return {
        db_get: (nsPtr, keyPtr, cbId, ctx) => {
            const ns = readStringPtr(h, nsPtr);
            const key = readStringPtr(h, keyPtr);
            const epoch = asyncOps.epochNow();
            asyncOps.track(kv.get(scopeOf(ns), ns, key).then(v => asyncOps.pushDbGet(epoch, {
                cbId, ctx, ns, key,
                success: v !== undefined,
                data: v ?? new Uint8Array(0),
            }), err => asyncOps.pushDbGet(epoch, {
                cbId, ctx, ns, key,
                success: false, data: new Uint8Array(0), infraError: errMsg(err),
            })));
        },
        db_set: (nsPtr, keyPtr, dataPtr, cbId, ctx) => {
            const ns = readStringPtr(h, nsPtr);
            const key = readStringPtr(h, keyPtr);
            // readBytesPtr copies, so the wasm memory can grow (under a later
            // op resolving) without invalidating `data`.
            const data = readBytesPtr(h, dataPtr);
            const epoch = asyncOps.epochNow();
            asyncOps.track(kv.set(scopeOf(ns), ns, key, data).then(result => asyncOps.pushDbWrite(epoch, {
                op: "set", cbId, ctx, ns, key,
                success: result.ok,
                ...(result.infraError !== undefined ? { infraError: result.infraError } : {}),
            }), err => asyncOps.pushDbWrite(epoch, {
                op: "set", cbId, ctx, ns, key, success: false, infraError: errMsg(err),
            })));
        },
        db_del: (nsPtr, keyPtr, cbId, ctx) => {
            const ns = readStringPtr(h, nsPtr);
            const key = readStringPtr(h, keyPtr);
            const epoch = asyncOps.epochNow();
            asyncOps.track(kv.del(scopeOf(ns), ns, key).then(() => asyncOps.pushDbWrite(epoch, { op: "del", cbId, ctx, ns, key, success: true }), err => asyncOps.pushDbWrite(epoch, {
                op: "del", cbId, ctx, ns, key, success: false, infraError: errMsg(err),
            })));
        },
        db_list: (nsPtr, prefixPtr, cbId, ctx, startPtr, limit, reverse) => {
            const ns = readStringPtr(h, nsPtr);
            const prefix = readStringPtr(h, prefixPtr);
            // `start` is `optional` with no int/bool default, so an omitted arg
            // arrives as a null pointer (codegen's i32Const(0)); the heap never
            // allocates at 0, so ptr === 0 unambiguously means the empty cursor.
            const start = startPtr === 0 ? "" : readStringPtr(h, startPtr);
            const rev = reverse !== 0;
            const epoch = asyncOps.epochNow();
            asyncOps.track(kv.list(scopeOf(ns), ns, prefix, start, limit, rev).then(
            // The canonical ordered-page definition, applied here at the single
            // choke point every tier funnels through, so the script sees a
            // DETERMINISTIC page regardless of each backend's native iteration
            // order. A backend that pushed the work down (a SQL range query)
            // already returns this exact page, so pageKeys is idempotent on it;
            // one that returns the whole matching set (in-memory oracle default)
            // gets sorted + cursored + limited here. Order is UTF-16 code-unit
            // sort — spec-defined and identical on every JS engine — so db_list
            // is byte-identical across tiers, which the fidelity rig relies on.
            keys => asyncOps.pushDbList(epoch, {
                cbId, ctx, ns, prefix, success: true, keys: pageKeys(keys, prefix, start, limit, rev),
            }), err => asyncOps.pushDbList(epoch, {
                cbId, ctx, ns, prefix, success: false, keys: [], infraError: errMsg(err),
            })));
        },
    };
}
function errMsg(e) {
    return e instanceof Error ? e.message : String(e);
}
export function makeClientCommonEnv(h, hooks) {
    const env = {
        // Emulator memory — wider reads compose bytes little-endian (NES +
        // Game Boy are LE).
        read_u8: (a) => hooks.onMemRead(a, 1, false),
        read_u16: (a) => hooks.onMemRead(a, 2, false),
        read_u32: (a) => hooks.onMemRead(a, 4, false),
        read_i8: (a) => hooks.onMemRead(a, 1, true),
        read_i16: (a) => hooks.onMemRead(a, 2, true),
        read_i32: (a) => hooks.onMemRead(a, 4, true),
        write_u8: (a, v) => hooks.onMemWrite(a, v, 1),
        write_u16: (a, v) => hooks.onMemWrite(a, v, 2),
        write_u32: (a, v) => hooks.onMemWrite(a, v, 4),
        write_i8: (a, v) => hooks.onMemWrite(a, v, 1),
        write_i16: (a, v) => hooks.onMemWrite(a, v, 2),
        write_i32: (a, v) => hooks.onMemWrite(a, v, 4),
        // The server-side send shape is a type error in `client {}` —
        // reaching this binding means a script bug, not a host gap.
        send_server: () => {
            console.error(`[${hooks.label}] script error: send(to, ...) called in client-side code`);
        },
        image_literal: (w, ht, dataPtr) => hooks.onImageLiteral(w, ht, dataPtr),
        image_from_bytes: (bytesPtr) => hooks.onImageFromBytes(bytesPtr),
        image_from_png_bytes: (dataPtr, x, y, w, ht) => hooks.onImageFromPngBytes(dataPtr, x, y, w, ht),
        palette_from_png_bytes: (dataPtr) => hooks.onPaletteFromPngBytes(dataPtr),
        image_from_bitmap_bytes: (dataPtr, dx, dy) => hooks.onImageFromBitmapBytes(dataPtr, dx, dy),
        drawImage: (img, pal, x, y, flipX, flipY, clipX, clipY, clipW, clipH, clipRectX1, clipRectY1, clipRectX2, clipRectY2, behindBackground) => {
            hooks.onDrawCall({
                img, pal, x, y,
                flipX: !!flipX, flipY: !!flipY,
                clipX, clipY, clipW, clipH,
                clipRectX1, clipRectY1, clipRectX2, clipRectY2,
                behindBackground: !!behindBackground,
            });
        },
        // Client-visible deployment surface.
        max_clients: () => hooks.maxClients,
        tick_rate: () => 30,
        // Hex SHA-1 of the loaded ROM. Encoded fresh per call; the script
        // typically reads it once on connect.
        local_rom_hash: () => allocString(h, hooks.romHash),
        // Visible viewport of the client's console, in pixels (NES 256x240,
        // Game Boy 160x144, …). Lets a UI script lay itself out for the
        // actual screen instead of hard-coding one console's dimensions.
        screen_width: () => hooks.screenWidth,
        screen_height: () => hooks.screenHeight,
        // ROM control. The frame loop and `event frame` keep running while
        // paused — the visible effect is that the underlying game state
        // stops advancing but overlay UI continues to update.
        pause_rom: () => hooks.pauseRom(),
        resume_rom: () => hooks.resumeRom(),
        // Input — polling reads the frame-start snapshot, so multiple
        // checks inside one event frame see a consistent view (per
        // docs/language.md "Input"). consume_input only takes effect when
        // an event_client_input handler is on the stack; outside that the
        // call is logged and ignored (a real trap would be cleaner, but we
        // don't surface wasm traps across the host boundary today).
        input_held: (b) => hooks.input.isHeld(b) ? 1 : 0,
        input_pressed: (b) => hooks.input.isPressed(b) ? 1 : 0,
        consume_input: () => {
            if (!hooks.input.consume()) {
                console.error(`[${hooks.label}] consume_input() called outside event input handler`);
            }
        },
        ...makeDbEnv(h, hooks.asyncOps, hooks.persistence, hooks.persistenceScope),
        // Coordinate constructors for the execution-hook builtins — pure
        // host-side encoders (no core needed), so always present. prg()/bank()
        // are PRG_ROM; cpu() is the raw-address escape hatch.
        prg: (offset) => encodeCoord(0, offset),
        bank: (n, addr) => encodeCoord(0, n * 0x4000 + (addr & 0x3fff)),
        cpu: (addr) => encodeCoord(2, addr),
    };
    // Actuator builtins — bound only when the core implements
    // ExecHookCapable; otherwise they fall through to makeBaseEnv's
    // warn-once stubs (see the §execution-hooks note on ClientEnvHooks).
    // The `!` is safe: each binding is only added inside its presence guard,
    // and the guards don't narrow into the deferred closure body.
    if (hooks.onExecHook) {
        env.on_exec = (targetEnc, cbId, priority) => hooks.onExecHook(decodeCoord(targetEnc), cbId, priority);
    }
    if (hooks.clearExecHook) {
        env.clear_exec = (handle) => { hooks.clearExecHook(handle); };
    }
    if (hooks.onReadReg) {
        env.read_reg = (reg) => hooks.onReadReg(reg);
    }
    if (hooks.onWriteReg) {
        env.write_reg = (reg, val) => { hooks.onWriteReg(reg, val); };
    }
    if (hooks.onWriteRom) {
        env.write_rom = (coordEnc, byte) => { hooks.onWriteRom(decodeCoord(coordEnc), byte); };
    }
    if (hooks.onPpuRead) {
        env.read_ppu = (addr) => hooks.onPpuRead(addr);
    }
    if (hooks.onPpuWrite) {
        env.write_ppu = (addr, value) => { hooks.onPpuWrite(addr, value); };
    }
    // Sound builtins — bound only when the host wires audio; otherwise they
    // fall through to makeBaseEnv's stubs (sound_from_vgm → null handle,
    // play_sound → no-op). See the §sound note on ClientEnvHooks.
    if (hooks.onSoundFromVgm) {
        env.sound_from_vgm = (dataPtr) => hooks.onSoundFromVgm(dataPtr);
    }
    if (hooks.onSoundPlay) {
        env.play_sound = (snd, loop) => {
            hooks.onSoundPlay({ snd, loop: !!loop });
        };
    }
    // Battery-save toggle — bound only when the host persists saves; otherwise
    // falls through to makeBaseEnv's no-op stub. bool arrives as an i32 (0/1).
    if (hooks.setBatteryPersist) {
        env.battery_persist = (enabled) => { hooks.setBatteryPersist(enabled !== 0); };
    }
    return env;
}
//# sourceMappingURL=env-bindings.js.map